import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { WebsiteName } from '@/types/website';
import { WEBSITE_LABELS } from '@/types/website';

interface ContentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (content: { title: string; slug: string; website: WebsiteName }) => void;
  defaultWebsite?: WebsiteName;
}

export function ContentModal({ open, onOpenChange, onSave, defaultWebsite }: ContentModalProps) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [website, setWebsite] = useState<WebsiteName>(defaultWebsite || 'greenvision');

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slug || slug === generateSlug(title)) {
      setSlug(generateSlug(value));
    }
  };

  const handleSave = () => {
    if (!title.trim() || !slug.trim()) return;
    onSave({ title: title.trim(), slug: slug.trim(), website });
    setTitle('');
    setSlug('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nieuwe pagina aanmaken</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Titel *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Pagina titel"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">Slug *</Label>
            <div className="flex items-center">
              <span className="text-sm text-muted-foreground mr-1">/</span>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => setSlug(generateSlug(e.target.value))}
                placeholder="pagina-slug"
                className="font-mono text-sm"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Dit wordt het URL-pad van de pagina
            </p>
          </div>

          <div className="space-y-2">
            <Label>Website</Label>
            <Select value={website} onValueChange={(v) => setWebsite(v as WebsiteName)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(WEBSITE_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuleren
          </Button>
          <Button onClick={handleSave} disabled={!title.trim() || !slug.trim()}>
            Aanmaken
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
